import { Address } from './address'
import { IdentityVerification } from './identity-verification'
import { QuarterlyTaxReturnDTO } from './quarterly-tax-return'
import { Subscription, SubscriptionTaxRegime } from './subscription'
import { TaxRegistrationDTO } from './tax-registration'
import { UserDiscount } from './user-discount'
import { YoungerDependentRelationshipTypes } from './younger-dependent'

export const Variant = {
    quarterlyTaxReturnsAnnualSummary: 'quarterlyTaxReturnsAnnualSummary',
    taxReturn: 'taxReturn',
    selfEmployed: 'selfEmployed',
    newSelfEmployed: 'newSelfEmployed',
    existingSelfEmployed: 'existingSelfEmployed',
    company: 'company',
    newCompany: 'newCompany',
    existingCompany: 'existingCompany',
    bookkeeping: 'bookkeeping',
} as const

export type VariantType = (typeof Variant)[keyof typeof Variant]

type MaritalStatusType = 'Single' | 'Married' | 'Divorced' | 'Widowed' | 'DeFactoCouple'

type LanguagePreferenceType = 'ES' | 'EN'

type DisabilityGradeType =
    | 'NoDisability'
    | 'DisabilityOver33LessThan65Percent'
    | 'DisabilityOver33LessThan65PercentRequiresAssistance'
    | 'DisabilityOver65Percent'
    | 'JudicialIncapacitation'

export interface UserProfileDTO {
    id: number
    accountantId: number
    currentTaxReturnId: number
    email: string
    externalId: string
    firstName: string
    lastName: string
    previousLastName: string
    phoneNumber: string
    socialSecurityNumber: string
    uniqueTaxpayerReference: string
    accountantFullName: string
    referralSchemeCode: string
    referralSource: string
    initialVariant: VariantType | null
    variant: VariantType | null
    taxReturnCell: string
    supportNumber: string
    accountantUseIdentityVerification: boolean
    hasNoSocialSecurityNumber: boolean
    hasPendingSubscriptionWelcomeCall: boolean
    isLivingWithDependentsUnder25: boolean
    isLivingWithDependentsOver65: boolean
    isSelfEmployed: boolean | null
    isNewSelfEmployed: boolean | null
    isOnboarding: boolean
    emailMarketingConsent: boolean
    isMissingAgreement: boolean
    availableDiscountPercentage: number
    numberOfUnreadMessages: number
    changeNameDate: string
    dateOfBirth: string
    lastLoggedInDateUTC: string
    lastViewedMessagesUTC: string
    maritalStatusDate: string
    userIdentityCardExpiryDate: string
    agentAuthorisationStatus: string // @TODO
    customerType: string // @TODO
    disabilityGrade: DisabilityGradeType | null
    identityVerificationStatus?: IdentityVerification['status']
    languagePreference: LanguagePreferenceType
    maritalStatus: MaritalStatusType | null
    subscriptionTaxRegime: SubscriptionTaxRegime
    latestSubscription: Subscription
    referredPartnerUserHasPaidCurrentYearTaxReturn: boolean
    referrerPartnerUserHasPaidCurrentYearTaxReturn: boolean
    selfDeclaredAttributionSource: string // @TODO
    taxRegistrationStatus?: TaxRegistrationDTO['status']
    externalApis: string[] // @TODO
    isEligibleForSubscriptionFreeTrial: boolean
    isCompany: boolean
    isNewCompany: boolean
    addresses: Address[]
    olderDependents: {
        id: number
        userId: number
        firstName: string
        lastName: string
        uniqueTaxpayerReference: string
        canBeIncludedInJointTaxReturn: boolean
        dateOfBirth: string
        disabilityGrade: DisabilityGradeType | null
        relationshipToTaxpayer: string // @TODO
    }[]

    quarterlyTaxReturns: QuarterlyTaxReturnDTO[]
    subscriptions: Subscription[]
    taxReturns: {
        id: number
        taxYear: number
        accountantId: number
        accountantEmail: string
        accountantFirstName: string
        accountantFullName: string
        additionalNotes: string
        statusChangedDateUTC: number
        filedDateUTC: number
        ngoContributionType: string // @TODO
        status: string // @TODO
        dataImportedFromTaxOffice: boolean
        defaultsFromPreviousReturn: boolean
        hasDownloadedReturn: boolean
        hasPaid: boolean
        jointTaxReturn: boolean
        lateFiling: boolean
        lockedForUserEditing: boolean
        payTaxInInstalments: boolean
        payAfterFiling: boolean
        payAfterFilingAllowed: boolean
        price: number
        taxDue: number
        totalIncome: number
        totalTaxableIncome: number
        totalTaxDue: number
        deductions: {
            id: number
            isDeductible: boolean
            type: string // @TODO
        }[]

        hasEmployment: boolean
        hasSelfEmployment: boolean
        hasPropertyIncome: boolean
        hasCapitalGains: boolean
        hasDividendsOrInterest: boolean
        hasDonations: boolean
        hasPensionContributions: boolean
    }[]

    quarterlyTaxReturnsAnnualSummaries: {
        id: number
        userId: number
        status: string // @TODO
        statusChangedDateUTC: string
        cancellationDateUTC: string
        submittedDateUTC: string
        filedDateUTC: string
        taxYear: string
        price: number
        hasPaid: boolean
        additionalNotes: string
        lockedForUserEditing: boolean
        accountantId: number
        accountantEmail: string
        accountantFullName: string
    }[]

    userDiscounts: UserDiscount[]

    youngerDependents: {
        id: number
        userId: number
        firstName: string
        lastName: string
        uniqueTaxpayerReference: string
        dateOfBirth: string
        disabilityGrade: DisabilityGradeType | null
        gender: string // @TODO
        isAdopted: boolean
        canBeIncludedInJointTaxReturn: boolean
        relationshipToTaxpayers: YoungerDependentRelationshipTypes
    }[]

    hmacToken: string
    customerOwnershipType: CustomerOwnershipTypes
}

export interface UserProfileSlice extends Partial<UserProfileDTO> {
    fetchSuccessful: boolean | null
    shouldFetch: boolean
}

export interface UserProfileModelDTO {
    id: number
    accountantId: number | null
    accountantFullName: string | null
    email: string
    firstName: string
    lastName: string
    previousLastName: string
    phoneNumber: string
    socialSecurityNumber: string
    uniqueTaxpayerReference: string
    variant: VariantType | null
    taxReturnCell: string
    supportNumber: string
    hasNoSocialSecurityNumber: boolean
    isLivingWithDependentsUnder25: boolean | null
    isLivingWithDependentsOver65: boolean | null
    isSelfEmployed: boolean | null
    isNewSelfEmployed: boolean | null
    changeNameDate: string | null
    dateOfBirth: string | null
    lastViewedMessagesUTC: string | null
    maritalStatusDate: string | null
    userIdentityCardExpiryDate: string | null
    customerType: string
    disabilityGrade: DisabilityGradeType | null
    maritalStatus: MaritalStatusType | null
    selfDeclaredAttributionSource: string | null
    emailMarketingConsent: boolean
    isMissingAgreement: boolean
}

export interface CustomerTypeUpdateDTO {
    customerType: 'Individual' | 'Company'
}

export const CustomerOwnershipType = {
    TaxFix: 'TaxFix',
    TaxfixViaAccountantReferral: 'TaxfixViaAccountantReferral',
    Accountant: 'Accountant',
} as const

export type CustomerOwnershipTypes = (typeof CustomerOwnershipType)[keyof typeof CustomerOwnershipType]
